import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import React, { useState } from "react";
import { AXIOS_POST } from "../../server/api/crud";
import Swal from "sweetalert2";
import Dropzone2 from "../Dropzone2";
import { emailDocumentenUpload, emailHTML } from "../../EmailTemplates";
import { documentenKeuzen, sendEmail, sendEmailGewoon } from "../../function";
import SelectAdmins2 from "../SelectAdmins2";
import { getAdmins } from "../../Localstorage/localstorage";

export default function UploadDocumenten({
  open = false,
  setOpen,
  setRefresh,
  user,
  patient,
  type = "uploads",
}) {
  const admins = getAdmins();
  const [selectedAdmin, setSelectedAdmin] = useState(admins[0]);

  const [progresses, setProgresses] = useState(null); // Track progress for each file
  const [documenten, setDocumenten] = useState([]);

  const reset = () => {
    setProgresses({});
    setDocumenten([]);
    setOpen(false);
  };

  const saveDocuments = async () => {
    if (!documenten || documenten.length === 0) return;

    if (!selectedAdmin && user?.role_id == 2) {
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `Selecteer een specialist aan wie u toegang tot deze documenten wilt verlenen.`,
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    // Split documents into valid (<=6MB) and tooLarge (>6MB)
    const tooLargeDocuments = documenten.filter(
      (doc) => doc.size > 150 * 1024 * 1024
    );
    const validDocuments = documenten.filter(
      (doc) => doc.size <= 150 * 1024 * 1024
    );

    if (validDocuments.length === 0 && tooLargeDocuments.length > 0) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: `Alle bestanden zijn groter dan 150 MB en zijn niet opgeslagen.`,
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }

    const finalDocument = validDocuments.map((document) => ({
      ...document,
      title: progresses[document?.bestand]?.title || document.title,
    }));

    const url =
      user?.role_id === 2
        ? "documents/multiple"
        : type === "advies"
        ? "adviezen/multiple"
        : "documents/admin/multiple";

    const pagina = documentenKeuzen(user?.role_id)[type];

    try {
      const resp = await AXIOS_POST(url, { documenten: finalDocument });

      if (resp.data.success) {
        setRefresh(true);
        // Show success alert for saved files
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: `Bestanden zijn opgeslagen${
            tooLargeDocuments.length > 0
              ? `, ${tooLargeDocuments.length} bestand(en) waren te groot (>6MB) en zijn niet opgeslagen`
              : ""
          }`,
          showConfirmButton: false,
          timer: 500,
        });

        reset();

        const recipient =
          (user?.role_id === 1 && patient?.email && patient) ||
          (user?.role_id === 2 && selectedAdmin?.email && selectedAdmin);

        if (recipient) {
          const emailData = {
            from_name: "RepeatConnect",
            receiver: recipient.full_name,
            sender_email: "info@repeathealth.com",
            title: `Hallo ${recipient.full_name}, U heeft ${
              tooLargeDocuments?.length > 0
                ? `, ${tooLargeDocuments.length} bestanden ontvangen!`
                : "een bestand ontvangen!"
            }`,
            receiver_email: recipient.email,
            object: pagina,
            name: user?.full_name,
            bericht:
              tooLargeDocuments?.length > 1
                ? `${tooLargeDocuments.length} documenten`
                : "een document",
            inloglink: "https://repeatconnect.com",
          };

          const emailHtml = emailDocumentenUpload(emailData);

          sendEmailGewoon("template_qdoh9mv", {
            message_html: emailHtml,
            title: emailData.title,
            receiver_email: emailData.receiver_email,
          });
        } else {
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: `De klant heeft nog geen e-mailadres toegevoegd aan zijn profielinstellingen.`,
            showConfirmButton: false,
            timer: 3500,
          });
        }
      }
    } catch (error) {
      console.error(
        "Error occurred:",
        error.response?.data || error.message || error
      );

      Swal.fire({
        position: "top-end",
        icon: "error",
        title: `Er is iets misgelopen`,
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        setOpen(false);
      }}
      className="relative z-10"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-700 px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 w-full sm:max-w-5xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="w-full pt-1">
              <div className="flex-row flex justify-between mb-2">
                <h2 className="mt-2 dark:text-gray-200 text-base font-semibold leading-6 text-gray-900 mb-4">
                  Laad een bestanden op
                </h2>
                {user?.role_id == 2 && (
                  <SelectAdmins2
                    selected={selectedAdmin}
                    setSelected={setSelectedAdmin}
                    data={admins}
                  />
                )}
                <button
                  onClick={() => {
                    setProgresses({});
                  }}
                  className="bg-neutral-200 dark:text-gray-200 dark:bg-gray-800 text-black ring-1 ring-neutral-600 text-sm rounded mr-2 mt-2 py-2 px-4"
                >
                  Verwijder ze allemaal
                </button>
              </div>
              <div>
                <Dropzone2
                  user={user}
                  setProgresses={setProgresses}
                  setDocumenten={setDocumenten}
                  patient={patient}
                  type={type}
                  progresses={progresses}
                />
              </div>
              <div className="flex justify-between">
                <button
                  onClick={() => setOpen(false)}
                  className="bg-gray-800 text-white text-sm rounded mr-2 mt-2 py-2 px-4"
                >
                  Sluiten
                </button>
                <div className="flex justify-end">
                  <button
                    onClick={saveDocuments}
                    className="bg-green-700 text-white text-sm rounded mr-2 mt-2 py-2 px-4"
                  >
                    Opslaan
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
